import { Link } from 'react-router';
import { motion } from 'motion/react';
import type { ProfileStatsDto } from '@redpandev/shared';
import {
  Avatar,
  Card,
  Disclosure,
  Mascot,
  fadeInUp,
  staggerContainer,
  tween,
  useReducedMotion,
} from '@redpandev/ui';
import { avatarSources } from '../lib/avatars';
import { CountUp } from './profile/CountUp';
import { LinkButton } from './profile/LinkButton';

/**
 * Stats du repaire : tuiles à gros chiffres en count-up, barre de ratio W/L
 * (mascotte curseur), détail par jeu et adversaires récents.
 * `owner=false` (profil public) → copy neutre, pas de CTA « lance un MOTUS ».
 * `collapsibleDetails` (page compte) → le détail est replié derrière
 * « Voir le détail » ; monté à l'ouverture (spec 08 §3).
 */
export function StatsBlock({
  stats,
  owner = true,
  collapsibleDetails = false,
}: {
  stats: ProfileStatsDto;
  owner?: boolean;
  collapsibleDetails?: boolean;
}) {
  if (stats.totalMatches === 0) return <EmptyStats owner={owner} />;

  const wins = stats.perGame.reduce((n, g) => n + g.wins, 0);
  const losses = stats.perGame.reduce((n, g) => n + g.losses, 0);
  const draws = stats.perGame.reduce((n, g) => n + g.draws, 0);
  const ratio = Math.round((wins / stats.totalMatches) * 100);

  const hasDetails = stats.perGame.length > 0 || stats.recentOpponents.length > 0;
  const details = hasDetails ? <StatsDetails stats={stats} /> : null;

  return (
    <div className="space-y-6">
      <motion.div
        variants={staggerContainer()}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      >
        <StatTile label="Parties" value={stats.totalMatches} icon="🎯" tone="text-panda-400" />
        <StatTile label="Victoires" value={wins} icon="🏆" tone="text-bamboo-400" />
        <StatTile label="Défaites" value={losses} icon="💥" tone="text-danger-500" />
        <StatTile
          label="Ratio de victoires"
          shortLabel="Ratio"
          value={ratio}
          icon="📈"
          tone="text-grape-400"
          format={(n) => `${n} %`}
        />
      </motion.div>

      <RatioBar wins={wins} losses={losses} draws={draws} total={stats.totalMatches} />

      {details &&
        (collapsibleDetails ? (
          <Disclosure label="Voir le détail" keepMounted={false}>
            <div className="pt-2">{details}</div>
          </Disclosure>
        ) : (
          details
        ))}
    </div>
  );
}

/** Détail : répartition par jeu + adversaires récents. */
function StatsDetails({ stats }: { stats: ProfileStatsDto }) {
  return (
    <div className="space-y-6">
      {stats.perGame.length > 0 && (
        <div>
          <h3 className="mb-2 font-display text-lg font-bold text-ink-100">Par jeu</h3>
          <ul className="space-y-2">
            {stats.perGame.map((g) => (
              <li
                key={g.game}
                className="flex items-center justify-between rounded-lg border border-ink-600/60 bg-surface px-4 py-2 text-sm"
              >
                <span className="font-display font-semibold uppercase text-panda-400">
                  {g.game}
                </span>
                <span className="font-mono text-ink-400">
                  <b className="text-bamboo-400">{g.wins}V</b> ·{' '}
                  <b className="text-danger-500">{g.losses}D</b> · {g.draws}N ({g.played})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {stats.recentOpponents.length > 0 && (
        <div>
          <h3 className="mb-2 font-display text-lg font-bold text-ink-100">Adversaires récents</h3>
          <ul className="flex flex-wrap gap-2">
            {stats.recentOpponents.map((o) => (
              <li key={o.displayName}>
                <Link
                  to={`/u/${encodeURIComponent(o.displayName)}`}
                  className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-full border border-ink-600/60 bg-surface py-1 pl-1 pr-3 text-sm font-semibold text-ink-100 transition-colors duration-fast ease-fun hover:border-panda-500/60"
                >
                  <Avatar {...avatarSources(o.avatarId)} size={32} />
                  {o.displayName}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  shortLabel,
  value,
  icon,
  tone,
  format = (n) => String(n),
}: {
  label: string;
  shortLabel?: string;
  value: number;
  icon: string;
  tone: string;
  format?: (n: number) => string;
}) {
  return (
    <Card
      role="group"
      aria-label={`${label} : ${format(value)}`}
      variants={fadeInUp}
      className="p-4 text-center"
    >
      {/* Contenu visuel masqué au SR : le label du groupe dit déjà tout (et le
          count-up ne fait pas lire ses valeurs intermédiaires). */}
      <div aria-hidden="true">
        <span className="text-xl">{icon}</span>
        <CountUp
          value={value}
          format={format}
          className={`block font-mono text-3xl font-bold ${tone}`}
        />
        <span className="text-xs uppercase tracking-wide text-ink-400">{shortLabel ?? label}</span>
      </div>
    </Card>
  );
}

/** Barre W/L : segments bambou/neutre/danger + mascotte curseur au % de victoires. */
function RatioBar({
  wins,
  losses,
  draws,
  total,
}: {
  wins: number;
  losses: number;
  draws: number;
  total: number;
}) {
  const reduced = useReducedMotion();
  const winPct = (wins / total) * 100;
  const drawPct = (draws / total) * 100;
  const label =
    `${wins} victoire${wins > 1 ? 's' : ''}, ${losses} défaite${losses > 1 ? 's' : ''}` +
    (draws > 0 ? `, ${draws} égalité${draws > 1 ? 's' : ''}` : '') +
    ` — ${Math.round(winPct)} % de victoires`;

  return (
    <div className="pt-7">
      <div className="relative">
        <motion.span
          aria-hidden="true"
          className="absolute -top-7 z-10 -translate-x-1/2"
          initial={reduced ? false : { left: '0%' }}
          animate={{ left: `${winPct}%` }}
          transition={tween.slow}
        >
          <Mascot mood={winPct >= 50 ? 'happy' : 'sad'} size={28} animated={false} />
        </motion.span>
        <div
          role="img"
          aria-label={label}
          className="flex h-3 overflow-hidden rounded-full bg-ink-700"
        >
          <motion.span
            className="h-full shrink-0 bg-bamboo-500"
            initial={reduced ? false : { width: '0%' }}
            animate={{ width: `${winPct}%` }}
            transition={tween.slow}
          />
          {draws > 0 && (
            <span className="h-full shrink-0 bg-ink-600" style={{ width: `${drawPct}%` }} />
          )}
          <span className="h-full flex-1 bg-danger-500" />
        </div>
      </div>
      <div className="mt-1.5 flex justify-between text-xs font-semibold" aria-hidden="true">
        <span className="text-bamboo-400">✓ {wins} V</span>
        {draws > 0 && <span className="text-ink-400">= {draws} N</span>}
        <span className="text-danger-500">✗ {losses} D</span>
      </div>
    </div>
  );
}

/** 0 partie : état vide charmant, mascotte endormie + CTA (page compte). */
function EmptyStats({ owner }: { owner: boolean }) {
  return (
    <Card className="flex flex-col items-center gap-4 p-8 text-center">
      <Mascot mood="sleepy" size={88} aria-label="Redpan endormi" />
      {owner ? (
        <>
          <p className="text-ink-400">
            Aucune partie pour l'instant… Réveille le panda&nbsp;: lance un Motus duel&nbsp;!
          </p>
          <LinkButton to="/motus" className="shadow-glow-panda">
            Lancer un Motus duel
          </LinkButton>
        </>
      ) : (
        <p className="text-ink-400">
          Ce panda fait la sieste&nbsp;: aucune partie jouée pour l'instant.
        </p>
      )}
    </Card>
  );
}
